import Shell from "../../../../../components/specific/Shell.tsx";
import { User } from "../../../../../api/accessControl/User.ts";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { BackendApi } from "../../../../../api/api.ts";
import { QueryKey } from "../../../../../api/queryKeys.ts";
import { usePartner__getClientVatDeclarationStatuses } from "../../../../../generated/blueprint.ts";
import { PaginationState, SortingState } from "../../../../../components/table/CreateTable.tsx";
import { ClientsTable } from "./ClientsTable.tsx";
import { DateRangeInput, DateRangeValue } from "../../../../../components/general/DateRangeInput/DateRangeInput.tsx";
import { DateRangePreset } from "../../../../../components/general/DateRangeInput/constants.ts";
import { formatDateForInput, getPresetRange } from "../../../../../components/general/DateRangeInput/utils/dateRangeUtils.ts";
import { useNavigate } from "react-router-dom";
import { ClientActivity, ClientBankAccount, ClientBankAccounts, ClientReport, ClientRow, ClientVatDeclarationStatus, VatDeclarationColumnState } from "./models.ts";
import { PrimaryButton } from "../../../../../components/general/PrimaryButton.tsx";
import { PlusIcon } from "@heroicons/react/24/outline";
import { DashboardKpiSubtitle } from "./components/clientTable/DashboardKpiSubtitle.tsx";
import Dropdown from "../../../../../components/general/Dropdown.tsx";
import { CLIENT_FILTER_OPTIONS, ClientFilter, filterClientRows } from "./clientFilters.ts";

const greetingEmojis = ['✨', '👋', '🙌', '🚀'];
const selectedGreetingEmoji = greetingEmojis[Math.floor(Math.random() * greetingEmojis.length)];

function vatDeclarationColumnState(canSeeVatDeclaration: boolean, isError: boolean, isPending: boolean): VatDeclarationColumnState {
    if (!canSeeVatDeclaration) {
        return 'hidden';
    }
    if (isError) {
        return 'failed';
    }
    return isPending ? 'loading' : 'loaded';
}

export default function PartnerDashboard({ currentUser }: { currentUser: User }) {

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [baseReports, setBaseReports] = useState<Array<ClientReport>>([]);
    const [activityById, setActivityById] = useState<Record<string, ClientActivity>>({});
    const [bankAccountsById, setBankAccountsById] = useState<Record<string, ClientBankAccount[]>>({});
    const [activityLoaded, setActivityLoaded] = useState<boolean>(false);
    const [banksLoaded, setBanksLoaded] = useState<boolean>(false);
    const [onboardingUrl, setOnboardingUrl] = useState<string>("");
    const [paginationState, setPaginationState] = useState<PaginationState<ClientRow>>({
        currentPage: 1,
        pageSize: 25
    });
    const [sortingState, setSortingState] = useState<SortingState>({
        orderBy: 'mdr_count',
        orderDescending: true
    });
    const [dateRange, setDateRange] = useState<DateRangeValue>(() => getPresetRange(DateRangePreset.LastQuarter));
    const [clientFilter, setClientFilter] = useState<ClientFilter>(ClientFilter.All);

    const api = new BackendApi(currentUser.getToken());
    const navigate = useNavigate();
    const canSeeVatDeclaration = currentUser.flags.getFlag('PARTNER__CAN_SEE_VAT_DECLARATION');
    const vatDeclarationsQuery = usePartner__getClientVatDeclarationStatuses(api, {
        queryKey: QueryKey.VatDeclarationSnapshots,
        enabled: canSeeVatDeclaration,
    });
    const vatDeclarationsById = useMemo(() => {
        const byId: Record<string, ClientVatDeclarationStatus> = {};
        vatDeclarationsQuery.data?.clients.forEach((entry) => {
            byId[entry.business_id] = entry;
        });
        return byId;
    }, [vatDeclarationsQuery.data]);
    const vatDeclarationState = vatDeclarationColumnState(canSeeVatDeclaration, vatDeclarationsQuery.isError, vatDeclarationsQuery.isPending);

    async function loadBase() {
        setIsLoading(true);
        try {
            const response = await api.Partner__getClientReports({});
            setBaseReports(response.client_reports);
            setOnboardingUrl(response.onboarding_url);
        } catch (error) {
            console.error("Error loading clients:", error);
        } finally {
            setIsLoading(false);
        }
    }

    async function loadBankAccounts() {
        setBanksLoaded(false);
        try {
            const response = await api.Partner__getClientBankAccounts({});
            const byId: Record<string, ClientBankAccount[]> = {};
            response.clients.forEach((entry: ClientBankAccounts) => {
                byId[entry.business_id] = entry.bank_accounts;
            });
            setBankAccountsById(byId);
        } catch (error) {
            console.error("Error loading bank accounts:", error);
        } finally {
            setBanksLoaded(true);
        }
    }

    async function loadActivity(range: DateRangeValue) {
        if (range.from == null || range.to == null) {
            return;
        }
        setActivityLoaded(false);
        try {
            const response = await api.Partner__getClientActivity(
                formatDateForInput(range.from),
                formatDateForInput(range.to)
            );
            const byId: Record<string, ClientActivity> = {};
            response.activities.forEach((entry: ClientActivity) => {
                byId[entry.business_id] = entry;
            });
            setActivityById(byId);
        } catch (error) {
            console.error("Error loading activity:", error);
        } finally {
            setActivityLoaded(true);
        }
    }

    useEffect(() => {
        loadBase();
        loadBankAccounts();
    }, []);

    useEffect(() => {
        loadActivity(dateRange);
    }, [dateRange.from, dateRange.to]);

    const clientRows: ClientRow[] = useMemo(() => baseReports.map((report) => {
        const activity = activityById[report.id];
        const transactionsCount = (activity?.inflow_transactions_count ?? 0) + (activity?.outflow_transactions_count ?? 0);
        const mdrCount = activity?.mdr_count ?? 0;
        return {
            ...report,
            transactions_count: transactionsCount,
            documents_uploaded_count: activity?.documents_uploaded_count ?? 0,
            mdr_count: mdrCount,
            completeness: transactionsCount > 0 ? ((transactionsCount - mdrCount) / transactionsCount) * 100 : 0,
            bank_accounts: bankAccountsById[report.id] ?? [],
            vat_declaration: vatDeclarationsById[report.id] ?? null,
        };
    }), [baseReports, activityById, bankAccountsById, vatDeclarationsById]);

    const visibleClientRows = useMemo(
        () => filterClientRows(clientRows, clientFilter),
        [clientRows, clientFilter]
    );

    function selectClientFilter(filter: ClientFilter) {
        setClientFilter(filter);
        setPaginationState((previous) => ({ ...previous, currentPage: 1 }));
    }

    return (
        <Shell
            currentUser={currentUser}
            isLoading={isLoading}
            title={
                <div className="w-full flex items-start justify-between">
                    <div>
                        <h1 className="text-4xl font-normal tracking-tight text-gray-900">Hallo {currentUser.firstName} {selectedGreetingEmoji}</h1>
                        {clientRows.length > 0 && (
                            <DashboardKpiSubtitle clientReports={clientRows} />
                        )}
                    </div>
                    <div className="ml-auto flex flex-col items-end gap-8">
                        <DateRangeInput
                            value={dateRange}
                            onChange={setDateRange}
                            defaultPreset={DateRangePreset.LastQuarter}
                        />
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-y-2">
                <ClientsTable
                    currentUser={currentUser}
                    clientReports={visibleClientRows}
                    onboardingUrl={onboardingUrl}
                    activityLoaded={activityLoaded}
                    banksLoaded={banksLoaded}
                    vatDeclarationState={vatDeclarationState}
                    onClientSettingsClick={(client: ClientRow) => {
                        navigate(`/business/${client.id}`);
                    }}
                    paginationState={[paginationState, setPaginationState]}
                    sortingState={[sortingState, setSortingState]}
                    isFiltered={clientFilter !== ClientFilter.All}
                    filters={
                        <div className="w-52">
                            <Dropdown
                                selected={CLIENT_FILTER_OPTIONS.find((option) => option.value === clientFilter) ?? CLIENT_FILTER_OPTIONS[0]}
                                options={CLIENT_FILTER_OPTIONS}
                                onUpdateFunc={(value) => selectClientFilter(value as ClientFilter)}
                            />
                        </div>
                    }
                    actions={
                        <PrimaryButton
                            title="Bedrijf toevoegen"
                            icon={PlusIcon}
                            onClick={() => navigate("/add-business")}
                        />
                    }
                />
            </div>
        </Shell>
    );
}
